/**
 * Repair list item display helpers — support Spectora Observation items and
 * Attik-native tagged items without converting Attik → Spectora.
 */

import type { AttikReportObservation } from '@/app/client/reports/v2/attik/attikReportTypes';
import {
  observationDisplayHtml,
  observationDisplayText,
  observationMedia,
} from '@/app/client/reports/v2/attik/attikReportTypes';
import { observationTitle } from '@/app/client/reports/v2/attik/attikSeverity';
import type {
  AttikRepairItem,
  AttikRepairObservation,
  RepairItem,
  SpectoraRepairPhoto,
} from '@attik/types/repairList.types';
import {
  isAttikRepairItem,
  isSpectoraRepairItem,
} from '@attik/types/repairList.types';

export { isAttikRepairItem, isSpectoraRepairItem };

function asAttikObs(obs: AttikRepairObservation): AttikReportObservation {
  return obs as AttikReportObservation;
}

export function repairItemId(item: RepairItem): string {
  return item.id;
}

export function repairItemName(item: RepairItem): string {
  if (isAttikRepairItem(item)) {
    const skinName = item.observation.legacySkin?.name?.trim();
    if (skinName) return skinName;
    if (item.observation.title?.trim()) return item.observation.title.trim();
    return observationTitle(asAttikObs(item.observation));
  }
  return item.name;
}

export function repairItemItemName(item: RepairItem): string {
  if (isAttikRepairItem(item)) return item.itemName;
  return item.item_name ?? item.name;
}

export function repairItemLocation(
  item: RepairItem,
): string | null | undefined {
  if (isAttikRepairItem(item)) return item.observation.location ?? null;
  return item.location;
}

export function repairItemTextHtml(item: RepairItem): string | null {
  if (isAttikRepairItem(item)) {
    const skinText = item.observation.legacySkin?.text?.trim();
    if (skinText) return skinText;
    const html = observationDisplayHtml(asAttikObs(item.observation));
    return html || null;
  }
  return item.text ?? null;
}

export function repairItemTextPlain(item: RepairItem): string {
  if (isAttikRepairItem(item)) {
    return observationDisplayText(asAttikObs(item.observation));
  }
  const raw = item.text ?? '';
  return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function repairItemRecommendation(
  item: RepairItem,
): string | undefined {
  if (isAttikRepairItem(item)) {
    // Dir1 Attik observations have no recommendation field without legacySkin.
    return item.observation.legacySkin?.recommendation?.trim() || undefined;
  }
  return item.recommendation;
}

/**
 * Spectora: `recommendation_id`. Attik: `templateCommentId`.
 * ProPair specialty match expects company `recommendedIds` to use the same id space
 * as the item source (Spectora rec ids vs Attik template comment ids).
 */
export function repairItemRecommendationId(
  item: RepairItem,
): string | number | undefined {
  if (isAttikRepairItem(item)) {
    return item.observation.templateCommentId;
  }
  return item.recommendation_id;
}

export function repairItemCommentType(item: RepairItem): string {
  if (isAttikRepairItem(item)) return item.observation.commentType;
  return item.comment_type ?? '';
}

/** Attik defect level / legacySkin → portal category (-1 / 0 / 1). */
function attikObservationCategory(obs: AttikReportObservation): number {
  const stored = obs.legacySkin?.category;
  if (typeof stored === 'number' && Number.isFinite(stored)) {
    if (stored === 2) return 0;
    if (stored === 3) return 1;
    if (stored === -1 || stored === 0 || stored === 1) return stored;
  }

  if (obs.commentType !== 'defect') return 0;

  const id = (obs.defectLevel || '').toLowerCase();
  if (
    id === 'maintenance' ||
    id === 'low' ||
    id.includes('maint') ||
    id.includes('monitor')
  ) {
    return -1;
  }
  if (id === 'medium' || id === 'med' || id.includes('repair')) return 0;
  if (
    id === 'high' ||
    id.includes('safety') ||
    id.includes('action') ||
    id.includes('immediate')
  ) {
    return 1;
  }

  return 1;
}

export function repairItemCategory(item: RepairItem): number {
  if (isAttikRepairItem(item)) {
    return attikObservationCategory(asAttikObs(item.observation));
  }
  return item.category ?? 0;
}

export function repairItemDefectIdx(item: RepairItem): string | undefined {
  if (isAttikRepairItem(item)) {
    const order = item.observation.order;
    if (typeof order === 'number' && Number.isFinite(order)) {
      return String(order);
    }
    return undefined;
  }
  return item.defectIdx;
}

export type RepairItemThumb = {
  id: string;
  url: string;
  caption?: string | null;
  isVideo?: boolean;
};

function spectoraPhotoUrl(photo: SpectoraRepairPhoto): string {
  return (
    photo.video_thumb ||
    photo.video ||
    photo.edited_image_url ||
    photo.original_image_url ||
    photo.image ||
    photo.small_image_url ||
    photo.photo ||
    ''
  );
}

export function repairItemThumbs(item: RepairItem): RepairItemThumb[] {
  if (isAttikRepairItem(item)) {
    return observationMedia(asAttikObs(item.observation)).map((m) => ({
      id: m.id,
      url: m.url,
      caption: m.caption ?? null,
      isVideo: /\.(mp4|mov|webm|m4v)(\?|$)/i.test(m.url),
    }));
  }
  return (item.photos ?? []).map((p) => {
    const url = spectoraPhotoUrl(p);
    return {
      id: p.id,
      url,
      caption: p.caption ?? null,
      isVideo: !!(
        p.video ||
        p.video_url ||
        /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url)
      ),
    };
  });
}

export function repairItemThumbUrl(item: RepairItem): string | undefined {
  const thumbs = repairItemThumbs(item);
  const first = thumbs[0];
  return first?.url || undefined;
}
